import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useHistory, useLocation, Redirect } from 'react-router-dom';
import { useIntl } from "react-intl";
import { LocalizationIds } from "helpers/Localizations";

interface IUsersContext {
    recordCount: number
    search: ISearchRequest
    users: IUser[]
    isLoading: boolean

    doSearch: (search: ISearchRequest) => void
    handleChangePage: (page: number) => void
    searchUsers: (searchRequest: ISearchRequest) => Promise<void>
    deleteUser: (userId: string) => Promise<void>
    restoreUser: (userId: string) => Promise<void>
    loginAs: (email: string) => void
    updateSearch: (request: ISearchRequest) => void
    reSendEmail: (userEmail: string) => Promise<void>
}

export interface IUser {
    id: string
    email: string
    fullName: string
    canGoToCustomer: boolean
    groups: string[]
    isDeleted: boolean
    canDoLoginAs: boolean
}

interface ISearchRequest {
    name: string
    email: string
    showOnly: 'active' | 'deleted'
    groups: string[]
    pageNr: number
    pageSize: number
}

export let usersContext = React.createContext({} as IUsersContext);

let { Provider } = usersContext;

let UsersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { enqueueSnackbar } = useSnackbar();
    let history = useHistory();
    let location = useLocation();
    let { formatMessage } = useIntl();

    let [isLoading, setLoading] = useState(true);

    let [users, setUsers] = useState<IUser[]>([]);
    let [recordCount, setTotalRecords] = useState(0);

    let [search, setSearch] = useState<ISearchRequest>({
        name: '',
        email: '',
        showOnly: 'active',
        groups: [],
        pageNr: 0,
        pageSize: 15
    });

    useEffect(() => {
        if (location.state)
            processState(location.state);
    }, [location.state])

    if (!location.state)
        return <Redirect to={{ pathname: location.pathname, state: { search } }} />


    let processState = (state: any) => {
        if (state.search) {
            setSearch(state.search);
            searchUsers(state.search);
        }
    }

    let doSearch = (values: ISearchRequest) => {
        let request = {
            ...values,
            pageNr: values.pageNr > 0 ? 0 : values.pageNr
        }

        history.push(location.pathname, { search: request });
    }

    let searchUsers = async (searchRequest: ISearchRequest) => {
        try {
            setLoading(true);
            let response = await axios.post('/api/User/SearchUsers', searchRequest);
            setUsers(response.data.items);
            setTotalRecords(response.data.recordCount);
        } catch (error) { }
        finally {
            setLoading(false);
        }
    }

    let handleChangePage = (page: number) => {
        let request = {
            ...search,
            pageNr: page
        }

        window.scrollTo(0, 0);
        history.push(location.pathname, { search: request });
    }

    let loginAs = async (email: string) => {
        if (window.confirm("Vuoi simulare il login con l'utente selezionato?")) {
            window.location.href = '/api/auth/LoginAs?email=' + email;
        }
    }

    let deleteUser = async (userId: string) => {
        if (window.confirm(formatMessage({ id: LocalizationIds.DeleteUserConfirm })) === false)
            return;

        try {
            await axios.delete(`/api/User/DeleteUser/${userId}`)
            setUsers(users.filter(user => user.id !== userId));
            setTotalRecords(recordCount - 1);
            enqueueSnackbar("User deleted", { variant: 'success' });
        } catch (error) { }
    }

    let restoreUser = async (userId: string) => {
        if (window.confirm(formatMessage({ id: LocalizationIds.INFO_RestoreUserConfirm })) === false)
            return;

        try {
            await axios.post('/api/User/RestoreUser', { userId });
            setUsers(users.filter(user => user.id !== userId));
            setTotalRecords(recordCount - 1);
            enqueueSnackbar("User restored", { variant: "success" });

        } catch (err) { }
    }

    let updateSearch = (request: ISearchRequest) => {
        setSearch(request);
    }

    let reSendEmail = async (userEmail: string) => {
        if (window.confirm(formatMessage({ id: LocalizationIds.ResendEmailInvitationConfirm })) === false)
            return;

        try {
            await axios.get(`/api/User/SendInvite/${userEmail}`)
            enqueueSnackbar("User invited", { variant: 'success' });
        } catch (error) { }
    }

    return (
        <Provider value={{
            recordCount,
            search,
            users,
            isLoading,

            doSearch,
            handleChangePage,
            searchUsers,
            deleteUser,
            restoreUser,
            updateSearch,
            loginAs,
            reSendEmail
        }}>
            {children}
        </Provider>
    );
};

export default UsersProvider;